import api from '@/core/api/client';
import { logger } from '@/core/logger/logger';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
    isActive: boolean;
    mustResetPassword?: boolean;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'SUPERADMIN' | 'SUPPORT' | 'FINANCE' | 'SALES';
  isActive: boolean;
  /** KLOCKSEC: 2FA (TOTP) ativo no staff. */
  twoFactorEnabled?: boolean;
  /** KLOCKSEC: senha fraca detectada no login → forçar troca. */
  mustResetPassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Login pode exigir: (a) desafio 2FA (já configurado), (b) enrollment
 * obrigatório (2FA mandatório e ainda não configurado), ou emitir a sessão.
 */
export type LoginResult =
  | { requires2fa: true; requires2faSetup?: false; tmpToken: string }
  | { requires2faSetup: true; requires2fa?: false; tmpToken: string }
  | { requires2fa?: false; requires2faSetup?: false; token: string; user: LoginResponse['user'] };

export interface TwoFactorSetup {
  secret: string;
  qrCodeDataUrl: string;
  otpAuthUrl: string;
}

export const authService = {
  async login(data: LoginRequest): Promise<LoginResult> {
    const response = await api.post('/auth/login', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Erro ao fazer login');
    }
    const payload = response.data.data;
    if (payload?.requires2fa) {
      return { requires2fa: true, tmpToken: payload.tmpToken };
    }
    if (payload?.requires2faSetup) {
      return { requires2faSetup: true, tmpToken: payload.tmpToken };
    }
    localStorage.setItem('token', payload.token);
    localStorage.setItem('user', JSON.stringify(payload.user));
    return { requires2fa: false, token: payload.token, user: payload.user };
  },

  /** Enrollment obrigatório — passo 1: gera o QR (usa o tmpToken do login). */
  async enroll2faStart(tmpToken: string): Promise<TwoFactorSetup> {
    const response = await api.post('/auth/2fa/enroll/start', { tmpToken });
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao iniciar 2FA');
    return response.data.data;
  },

  /** Enrollment obrigatório — passo 2: confirma, ativa e emite a sessão. */
  async enroll2faConfirm(tmpToken: string, code: string): Promise<{ user: User; backupCodes: string[] }> {
    const response = await api.post('/auth/2fa/enroll/confirm', { tmpToken, code });
    if (!response.data.success) throw new Error(response.data.message || 'Código inválido');
    const { token, user, backupCodes } = response.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    return { user, backupCodes };
  },

  /** Completa o login com o código 2FA (TOTP ou backup). */
  async verify2fa(tmpToken: string, code: string): Promise<User> {
    const response = await api.post('/auth/2fa/verify', { tmpToken, code });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Falha na verificação 2FA');
    }
    const { token, user } = response.data.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  },

  /** Inicia setup do 2FA (retorna QR). */
  async setup2fa(): Promise<TwoFactorSetup> {
    const response = await api.post('/auth/2fa/setup');
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao iniciar 2FA');
    return response.data.data;
  },

  /** Confirma o 2FA com o primeiro código; retorna os backup codes. */
  async confirm2fa(code: string): Promise<string[]> {
    const response = await api.post('/auth/2fa/confirm', { code });
    if (!response.data.success) throw new Error(response.data.message || 'Código inválido');
    return response.data.data.backupCodes as string[];
  },

  /** Desativa o 2FA (re-auth por senha). */
  async disable2fa(password: string): Promise<void> {
    const response = await api.post('/auth/2fa/disable', { password });
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao desativar 2FA');
  },

  async getMe(): Promise<User> {
    const response = await api.get('/auth/me');
    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Erro ao buscar perfil');
  },

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getToken(): string | null {
    return localStorage.getItem('token');
  },

  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      const user = JSON.parse(userStr) as Partial<User> | null;
      if (user && typeof user.id === 'string' && typeof user.name === 'string' && typeof user.email === 'string'
        && typeof user.isActive === 'boolean' && ['SUPERADMIN', 'SUPPORT', 'FINANCE', 'SALES'].includes(user.role ?? '')) return user as User;
    } catch {
      logger.warn('[auth] Invalid cached profile');
    }
    return null;
  },

  /** Troca da própria senha (fluxo de senha fraca forçada / KLOCKSEC). */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await api.post('/auth/change-password', { currentPassword, newPassword });
    if (!response.data?.success) {
      throw new Error(response.data?.message || 'Erro ao trocar senha');
    }
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },
};
